// src/screens/customer/customization/CustomizationPanel.jsx

import { useState, useRef, useCallback, useEffect, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Upload,
  X,
  Plus,
  ChevronRight,
  CheckCircle,
  MessageSquare,
  DollarSign,
  AlertCircle,
  Send,
  ImageIcon,
  Phone,
} from "lucide-react";
import "../../../assets/css/customer/shop/screens/CustomizationPannel.css";
import MyNavbar from "../../../components/customer/navigationbar/MyNavbar";
import Footer from "../../../subscreen/customer/footer/Footer";
import AppContext from "../../../context/AppContext";
import axios from "axios";

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const MAX_USER_IMAGES = 4;
const MAX_MB = 5;

// ─────────────────────────────────────────────────────────────
// SUCCESS SCREEN
// ─────────────────────────────────────────────────────────────
const SuccessScreen = ({
  requestId,
  productName,
  onGoHome,
  onViewRequests,
}) => (
  <div className="cp-success-wrap">
    <div className="cp-success-anim">
      <div className="cp-success-ring cp-ring-outer" />
      <div className="cp-success-ring cp-ring-inner" />
      <div className="cp-success-circle">
        <CheckCircle size={36} strokeWidth={1.8} />
      </div>
    </div>

    <h2 className="cp-success-title">Request Submitted!</h2>
    <p className="cp-success-msg">
      Your customization request for <strong>{productName}</strong> has been
      submitted. Our team will review and send you a quote within{" "}
      <strong>24–48 hours</strong>.
    </p>

    <div className="cp-success-id-block">
      <span className="cp-success-id-label">Request ID</span>
      <span className="cp-success-id-val">{requestId}</span>
    </div>

    <div className="cp-success-steps">
      {[
        { icon: "📋", title: "Request Received", desc: "Team notified" },
        { icon: "💬", title: "Quote Prepared", desc: "We review & price" },
        { icon: "✅", title: "You Accept", desc: "Confirm the quote" },
        { icon: "🚀", title: "Production Starts", desc: "We build your piece" },
      ].map((s, i) => (
        <div key={i} className="cp-success-step">
          <div className="cp-success-step-icon">{s.icon}</div>
          <p className="cp-success-step-title">{s.title}</p>
          <p className="cp-success-step-desc">{s.desc}</p>
        </div>
      ))}
    </div>

    <div className="cp-success-actions">
      <button className="cp-success-btn-sec" onClick={onGoHome}>
        Back to Shop
      </button>
      <button className="cp-success-btn-pri" onClick={onViewRequests}>
        View My Requests <ChevronRight size={14} />
      </button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const CustomizationPannel = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    baseUrlForFile,
    authToken,
    baseUrl,
    loggedInCustomer,
    RegExpChecker,
  } = useContext(AppContext);

  const product = location.state?.product;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ── Form state ──────────────────────────────────────────────
  const [description, setDescription] = useState("");
  const [phone, setPhone] = useState("");
  const [userImages, setUserImages] = useState([]);
  const [imgError, setImgError] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [requestId, setRequestId] = useState("");

  const inputRef = useRef(null);

  // ── Image handling ─────────────────────────────────────────
  const processFiles = useCallback(
    (files) => {
      setImgError("");
      const imageFiles = Array.from(files).filter((f) =>
        f.type.startsWith("image/"),
      );
      if (!imageFiles.length) return;

      const remaining = MAX_USER_IMAGES - userImages.length;
      if (remaining <= 0) {
        setImgError(
          `You can upload up to ${MAX_USER_IMAGES} reference images.`,
        );
        return;
      }

      const toAdd = [];
      for (const file of imageFiles.slice(0, remaining)) {
        if (file.size > MAX_MB * 1024 * 1024) {
          setImgError(`Each image must be under ${MAX_MB}MB.`);
          continue;
        }
        toAdd.push({
          id: Date.now() + Math.random(),
          file,
          preview: URL.createObjectURL(file),
        });
      }

      if (toAdd.length) setUserImages((prev) => [...prev, ...toAdd]);
      if (inputRef.current) inputRef.current.value = "";
    },
    [userImages],
  );

  const removeImage = (id) => {
    setUserImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.preview);
      return prev.filter((i) => i.id !== id);
    });
  };

  // ── Validation ──────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (description.trim().length < 20)
      e.desc =
        "Please write at least 20 characters describing your requirements.";

    if (!phone.trim()) {
      e.phone = "Please enter your contact number.";
    } else {
      const phoneErr = RegExpChecker("phone", phone.trim()).phone;
      if (phoneErr) e.phone = phoneErr;
    }

    setErrors(e);
    return !Object.keys(e).length;
  };

  // ── Submit ──────────────────────────────────────────────────
  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    const formdata = new FormData();
    formdata.append("description", description);
    formdata.append("price", product.price);
    formdata.append("quantity", 1);
    formdata.append("adproduct_id", product.id);
    formdata.append("status", "pending");
    formdata.append("type", "custom");
    formdata.append("total_price", 1 * product.price);
    formdata.append("adcustomer_id", loggedInCustomer?.id);
    formdata.append("phoneNo", phone);
    userImages.forEach((img) => formdata.append("images[]", img.file));

    axios
      .post(`${baseUrl}/customer/cart/createCustomCart`, formdata, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data.success) {
          setTimeout(() => {
            setRequestId("CR-" + (2000 + Math.floor(Math.random() * 999)));
            setIsSuccess(true);
          }, 1400);
        }
        setIsSubmitting(false);
      })
      .catch((error) => {
        console.log("error: ", error);
        setIsSubmitting(false);
      });
  };

  // ── Success screen ──────────────────────────────────────────
  if (isSuccess) {
    return (
      <div className="cp-page">
        <SuccessScreen
          requestId={requestId}
          productName={product.name}
          onGoHome={() => navigate("/products")}
          onViewRequests={() => navigate("/customer/custom-requests")}
        />
      </div>
    );
  }

  const productImgUrl = `${baseUrlForFile}/${product?.productimages?.[0]?.image_path}`;
  const slotsLeft = MAX_USER_IMAGES - userImages.length;

  return (
    <div className="cp-page">
      <MyNavbar />

      {/* ── Header ── */}
      <div className="cp-header">
        <div className="cp-header-text">
          <h1 className="cp-title">Customize This Product</h1>
          <p className="cp-subtitle">
            Describe your requirements and we'll build it just for you
          </p>
        </div>
      </div>

      {/* ── Main Layout ── */}
      <div className="cp-layout">
        {/* ════ LEFT — Form ════ */}
        <div className="cp-form-col">
          {/* Product banner */}
          <div className="cp-product-banner">
            <img
              src={productImgUrl}
              alt={product?.name}
              className="cp-product-banner-img"
            />
            <div className="cp-product-banner-info">
              <span className="cp-product-banner-tag">Customizing</span>
              <p className="cp-product-banner-name">{product?.name}</p>
              <p className="cp-product-banner-cat">
                {product?.adcategory?.name}
              </p>
            </div>
          </div>

          {/* ── Description ── */}
          <div className="cp-card">
            <div className="cp-field-head">
              <div className="cp-field-icon-wrap">
                <MessageSquare size={15} />
              </div>
              <div>
                <p className="cp-field-title">
                  Describe Your Requirements <span className="cp-req">*</span>
                </p>
                <p className="cp-field-sub">
                  Include style, color, material preferences, and any special
                  features
                </p>
              </div>
            </div>
            <div className="cp-textarea-wrap">
              <textarea
                className={`cp-textarea ${errors.desc ? "cp-input-error" : ""}`}
                rows={6}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setErrors((p) => ({ ...p, desc: "" }));
                }}
                maxLength={800}
                placeholder={
                  "Tell us exactly what you want:\n• Style — modern, classic, rustic, minimalist\n• Preferred colors or materials\n• Must-have features — storage, armrests, headboard, etc.\n• Any specific design inspiration"
                }
              />
              <span className="cp-char-count">{description.length} / 800</span>
            </div>
            {errors.desc && (
              <p className="cp-field-error">
                <AlertCircle size={13} /> {errors.desc}
              </p>
            )}
          </div>

          {/* ── Reference Images ── */}
          <div className="cp-card">
            <div className="cp-field-head">
              <div className="cp-field-icon-wrap">
                <ImageIcon size={15} />
              </div>
              <div>
                <p className="cp-field-title">
                  Reference Images{" "}
                  <span className="cp-img-count-label">
                    {userImages.length + 1} / {MAX_USER_IMAGES + 1}
                  </span>
                </p>
                <p className="cp-field-sub">
                  First slot is the product image. You can add up to{" "}
                  {MAX_USER_IMAGES} more.
                </p>
              </div>
            </div>

            <div className="cp-img-grid">
              {/* Slot 0 — locked product image */}
              <div className="cp-img-thumb cp-img-thumb--locked">
                <img src={productImgUrl} alt={product?.name} />
                <span className="cp-img-thumb-badge">Product</span>
              </div>

              {/* Slots 1–4 — customer uploaded images */}
              {userImages.map((img, i) => (
                <div key={img.id} className="cp-img-thumb">
                  <img src={img.preview} alt={`Reference ${i + 1}`} />
                  <button
                    type="button"
                    className="cp-img-thumb-remove"
                    onClick={() => removeImage(img.id)}
                    aria-label="Remove image"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}

              {slotsLeft > 0 && (
                <button
                  type="button"
                  className="cp-img-add-slot"
                  onClick={() => inputRef.current?.click()}
                >
                  <Plus size={20} />
                  <span>Add</span>
                </button>
              )}
            </div>

            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => processFiles([...e.target.files])}
            />

            {imgError && (
              <p className="cp-field-error">
                <AlertCircle size={13} /> {imgError}
              </p>
            )}

            <p className="cp-img-hint">
              First image is the product reference. Upload your own for extra
              context.
            </p>
          </div>
        </div>

        {/* ════ RIGHT — Sticky Summary ════ */}
        <div className="cp-summary-col">
          {/* ── Phone Number Box ── */}
          <div className="cp-card cp-phone-card">
            <div className="cp-field-head">
              <div className="cp-field-icon-wrap">
                <Phone size={15} />
              </div>
              <div>
                <p className="cp-field-title">
                  Contact Number <span className="cp-req">*</span>
                </p>
                <p className="cp-field-sub">
                  We'll use this to send your quote
                </p>
              </div>
            </div>
            <input
              type="tel"
              className={`cp-phone-input ${errors.phone ? "cp-input-error" : ""}`}
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                const err = RegExpChecker("phone", e.target.value).phone;
                setErrors((p) => ({ ...p, phone: e.target.value ? err : "" }));
              }}
              placeholder="e.g. 0312-3456789"
              maxLength={15}
            />
            {errors.phone && (
              <p className="cp-field-error">
                <AlertCircle size={13} /> {errors.phone}
              </p>
            )}
          </div>

          <div className="cp-summary-card cp-summary-details">
            <p className="cp-summary-heading">Request Summary</p>
            <div className="cp-summary-rows">
              <div className="cp-sum-row">
                <span className="cp-sum-label">Product</span>
                <span className="cp-sum-val">{product?.name}</span>
              </div>
              <div className="cp-sum-row">
                <span className="cp-sum-label">Description</span>
                <span className="cp-sum-val">
                  {description.trim().length > 0 ? (
                    description.length > 40 ? (
                      description.slice(0, 40) + "…"
                    ) : (
                      description
                    )
                  ) : (
                    <span className="cp-sum-empty">Not filled yet</span>
                  )}
                </span>
              </div>
              <div className="cp-sum-row">
                <span className="cp-sum-label">Contact</span>
                <span className="cp-sum-val">
                  {phone.trim() ? (
                    phone
                  ) : (
                    <span className="cp-sum-empty">Not filled yet</span>
                  )}
                </span>
              </div>
              <div className="cp-sum-row">
                <span className="cp-sum-label">Ref. Images</span>
                <span className="cp-sum-val">
                  {userImages.length + 1} / {MAX_USER_IMAGES + 1}
                </span>
              </div>
            </div>
          </div>

          <div className="cp-summary-card cp-info-note">
            <div className="cp-info-note-icon">💬</div>
            <div>
              <p className="cp-info-note-title">What happens next?</p>
              <p className="cp-info-note-text">
                Our team reviews your request and sends a personalised quote
                within <strong>24–48 hrs</strong>. You can accept and the order
                will be created automatically.
              </p>
            </div>
          </div>

          <button
            className="cp-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="cp-btn-spinner" /> Submitting Request...
              </>
            ) : (
              <>
                <Send size={15} /> Submit Customization Request
              </>
            )}
          </button>

          <p className="cp-submit-note">
            <CheckCircle size={11} /> No payment required at this stage
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CustomizationPannel;
